// SlotAvailabilityMatrix Component (Issue #966 - Phase 2A, Issue #1023)
// Calendar/heatmap visualization of slot availability over time for Fair Manager capacity planning

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fairManagerApi } from '../services/api';
import { SlotAvailabilityMatrix as SlotAvailabilityMatrixType } from '../types';
import { extractApiError } from '../utils/errorUtils'; // Issue #964

interface SlotAvailabilityMatrixProps {
  fairId?: number; // Optional: use this or get from route params
}

const SlotAvailabilityMatrix: React.FC<SlotAvailabilityMatrixProps> = ({ fairId: propFairId }) => {
  const { fairId: paramFairId } = useParams<{ fairId: string }>();
  const fairId = propFairId || (paramFairId ? parseInt(paramFairId, 10) : null);

  const [matrixData, setMatrixData] = useState<SlotAvailabilityMatrixType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMatrix = async () => {
      if (!fairId) {
        setError('Fair ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await fairManagerApi.getSlotAvailabilityMatrix(fairId);
        setMatrixData(data);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load slot availability matrix:', apiError);
        setError(apiError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMatrix();
  }, [fairId]);

  // Export to CSV
  const exportToCSV = () => {
    if (!matrixData) return;

    // Build CSV header
    const dates = Object.keys(matrixData.slots[0]?.dates || {}).sort();
    const header = ['Slot Number', ...dates].join(',');

    // Build CSV rows
    const rows = matrixData.slots.map(slot => {
      const availability = dates.map(date => (slot.dates[date] ? 'Available' : 'Reserved'));
      return [slot.slot_number, ...availability].join(',');
    });

    // Combine header and rows
    const csvContent = [header, ...rows].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `slot_availability_${matrixData.fair_name}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Revoke object URL to avoid memory leaks in long sessions
    URL.revokeObjectURL(url);
  };

  // Get color for availability status
  const getAvailabilityColor = (isAvailable: boolean): string => {
    return isAvailable ? 'bg-green-500' : 'bg-moonbone0';
  };

  // Get hover tooltip text
  const getTooltipText = (slotNumber: string, date: string, isAvailable: boolean): string => {
    const status = isAvailable ? 'Available' : 'Reserved';
    return `Slot ${slotNumber} - ${date}: ${status}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen" data-testid="availability-matrix-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8" data-testid="availability-matrix-error">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold mb-2">Error Loading Availability Matrix</h3>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!matrixData) {
    return null;
  }

  const dates = Object.keys(matrixData.slots[0]?.dates || {}).sort();

  return (
    <div className="container mx-auto px-4 py-8" data-testid="availability-matrix-container">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Slot Availability Matrix</h1>
        <p className="text-gray-600">
          {matrixData.fair_name} - {matrixData.start_date} to {matrixData.end_date}
        </p>
      </div>

      {/* Controls */}
      <div className="mb-4 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-sm text-gray-700">Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-moonbone0 rounded"></div>
            <span className="text-sm text-gray-700">Reserved</span>
          </div>
        </div>
        <button
          onClick={exportToCSV}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          data-testid="export-csv-button"
        >
          Export to CSV
        </button>
      </div>

      {/* Matrix Grid */}
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <div className="overflow-hidden border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200" data-testid="availability-matrix-table">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Slot
                  </th>
                  {dates.map((date) => (
                    <th
                      key={date}
                      scope="col"
                      className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {matrixData.slots.map((slot) => (
                  <tr key={slot.slot_id} className="hover:bg-gray-50">
                    <td className="sticky left-0 z-10 bg-white px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {slot.slot_number}
                    </td>
                    {dates.map((date) => (
                      <td
                        key={`${slot.slot_id}-${date}`}
                        className="px-3 py-4 whitespace-nowrap text-center"
                        title={getTooltipText(slot.slot_number, date, slot.dates[date])}
                      >
                        <div
                          className={`w-8 h-8 mx-auto rounded ${getAvailabilityColor(slot.dates[date])} hover:opacity-75 transition-opacity cursor-help`}
                          data-testid={`availability-cell-${slot.slot_id}-${date}`}
                        ></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-600">Total Slots</p>
            <p className="text-2xl font-bold text-gray-900">{matrixData.slots.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Dates</p>
            <p className="text-2xl font-bold text-gray-900">{dates.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Slot-Days</p>
            <p className="text-2xl font-bold text-gray-900">{matrixData.slots.length * dates.length}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlotAvailabilityMatrix;
